import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import { createHiddenInput } from 'helpers/form';

@registerElement('patient-checkin')
export default class PatientCheckinElement extends ImpulseElement {
  /**
   * Whether the element is active or not. If active, then attendance can be marked.
   */
  @property({ type: Boolean }) active = false;

  /**
   * An array of patient ids that were selected from the checkboxes.
   */
  @property({ type: Array }) selectedPatientIds: string[] = [];

  /**
   * The form name of the patient ids.
   */
  @property() patientIdsName: string;

  /**
   * Count of patients already checked in
   */
  @property({ type: Number }) checkedInCount = 0;

  @target() button: HTMLButtonElement;
  @target() checkinForm: HTMLFormElement;
  @target() clearForm: HTMLFormElement;
  @target() clearButton: HTMLElement;
  @target() clearDivider: HTMLElement;
  @target() checkinButton: HTMLButtonElement;
  @target() checkinNotice: HTMLElement;
  @target() alreadyCheckedInNotice: HTMLElement;
  @target() successMessage: HTMLElement;

  /**
   * Track whether we've modified the initial state
   */
  private initialStatePreserved = true;

  constructor() {
    super();
    this.handleDropdownShown = this.handleDropdownShown.bind(this);
    this.handleDropdownHidden = this.handleDropdownHidden.bind(this);
  }

  /**
   * Called when the element is added to the DOM.
   */
  connected() {
    $(this).on('shown.bs.dropdown', this.handleDropdownShown);
    $(this).on('hidden.bs.dropdown', this.handleDropdownHidden);

    // Parse the checked-in count attribute explicitly to ensure it's a number
    const checkedInCountAttr = this.getAttribute('data-checked-in-count');
    if (checkedInCountAttr) {
      this.checkedInCount = parseInt(checkedInCountAttr, 10);
    }

    if (this.active) {
      this.button.disabled = false;
    }

    // Check for pre-selected checkboxes and store their values
    this.captureInitialSelectedPatients();
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnected() {
    $(this).off('shown.bs.dropdown', this.handleDropdownShown);
    $(this).off('hidden.bs.dropdown', this.handleDropdownHidden);
  }

  /**
   * Called when the `active` property changes it's state.
   */
  activeChanged(active: boolean) {
    this.button.disabled = !active;
  }

  /**
   * Called when the selected patient ids change
   */
  selectedPatientIdsChanged(selectedPatientIds: string[]) {
    // Check if any patients are selected
    if (selectedPatientIds.length === 0) {
      this.active = false;
      return;
    }

    this.active = true;

    // Only update UI if we're no longer in the initial state
    // This prevents overriding the ERB-defined initial state
    if (!this.initialStatePreserved) {
      this.updateUIBasedOnCheckinStatus();
    }
  }

  /**
   * Hide the dropdown immediately when the form is submitted
   */
  hideDropdownBeforeSubmit(event) {
    const isAllPatientsSelected = this.isAllPatientsSelected();
    const totalPatientCount = this.getTotalPatientCount();

    // Show success message immediately for better UX
    if (event.target === this.checkinForm) {
      this.showSuccessMessage();

      // Add check icons immediately for selected patients
      this.addCheckIconsToSelectedPatients();

      // Update the checked-in count locally
      this.checkedInCount += this.selectedPatientIds.length;

      // Clear checkboxes if there is more than 1 patient in the form, regardless of how many are selected
      if (totalPatientCount > 1) {
        // First, directly unchecked the "check all" box if all patients were selected
        if (isAllPatientsSelected) {
          this.uncheckedCheckAllBox();
        }

        // Then clear all patient checkboxes
        this.clearCheckboxes();
      }
    } else if (event.target === this.clearForm) {
      // It's a clear action
      // Show success message with different text
      this.showClearSuccessMessage();

      // Remove check icons immediately
      this.removeCheckIconsFromSelectedPatients();

      // Update the checked-in count locally
      this.checkedInCount = 0;

      // Clear checkboxes if there is more than 1 patient in the form, regardless of how many are selected
      if (totalPatientCount > 1) {
        // First, directly unchecked the "check all" box if all patients were selected
        if (isAllPatientsSelected) {
          this.uncheckedCheckAllBox();
        }

        // Then clear all patient checkboxes
        this.clearCheckboxes();
      }
    }

    // Hide dropdown with delay to show success message
    setTimeout(() => {
      $(this).dropdown('hide');
    }, 1000);
  }

  /**
   * Called when the patient has been checkedin successfully.
   */
  checked(event: CustomEvent) {
    this.emit('checked', { detail: event.detail[0] });
  }

  /**
   * Find and store any pre-selected checkboxes
   */
  private captureInitialSelectedPatients() {
    const selectedCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]:checked');

    if (selectedCheckboxes.length > 0) {
      this.selectedPatientIds = Array.from(selectedCheckboxes).map(checkbox => checkbox.value);
      this.active = true;
    }
  }

  /**
   * Get the total count of patients in the form
   */
  private getTotalPatientCount(): number {
    const allCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]');
    return allCheckboxes.length;
  }

  /**
   * Check if all patients are selected
   */
  private isAllPatientsSelected(): boolean {
    const allCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]');
    const selectedCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]:checked');

    return allCheckboxes.length > 0 && allCheckboxes.length === selectedCheckboxes.length;
  }

  /**
   * Directly uncheck the "check all" box
   */
  private uncheckedCheckAllBox() {
    // Target the specific checkbox from your implementation
    const specificCheckAllBox = document.querySelector('input[data-checkbox-select-all-target="checkboxAll"]') as HTMLInputElement;
    if (specificCheckAllBox && specificCheckAllBox.checked) {
      specificCheckAllBox.checked = false;

      // Dispatch a change event to ensure any listeners update
      const changeEvent = new Event('change', { bubbles: true });
      specificCheckAllBox.dispatchEvent(changeEvent);
      return;
    }

    // Fallback to common selectors for "check all" checkboxes if the specific one isn't found
    const checkAllSelectors = [
      'input[data-action="patient-list#toggleAll"]',
      'input[data-action="change->checkbox-select-all#toggle"]',
      'input.check-all',
      'input.select-all',
      'input.custom-control-input[id$="-patient-list"]',
      'input[id="select_all"]',
      'input[id="check_all"]',
      'input[name="select_all"]',
      'input[name="check_all"]',
      'input[data-check-all="true"]',
      'input[data-select-all="true"]'
    ];

    // Try each selector and directly uncheck the first matching element we find
    for (const selector of checkAllSelectors) {
      const checkAllBox = document.querySelector(selector) as HTMLInputElement;
      if (checkAllBox && checkAllBox.checked) {
        checkAllBox.checked = false;

        // Also dispatch a change event on this checkbox to ensure any listeners update
        const changeEvent = new Event('change', { bubbles: true });
        checkAllBox.dispatchEvent(changeEvent);

        // If we found and updated one, we can exit
        return;
      }
    }
  }

  /**
   * Clear all checkboxes and the "check all" box
   */

  private clearCheckboxes() {
    // Clear individual patient checkboxes
    const checkboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]:checked');
    checkboxes.forEach(checkbox => {
      (checkbox as HTMLInputElement).checked = false;
    });

    // Clear any "check all" checkbox
    document.querySelectorAll('input[data-checkbox-select-all-target]').forEach(box => {
      const input = box as HTMLInputElement;
      if (input.checked || input.indeterminate) {
        input.checked = false;
        input.indeterminate = false;
      }
    });

    // Reset the selected patient ids
    this.selectedPatientIds = [];

    // Update the patient-attendance component
    const patientAttendanceElement = document.querySelector('patient-attendance[active]');
    if (patientAttendanceElement) {
      // Remove the active attribute
      patientAttendanceElement.removeAttribute('active');

      // Also update the selected-patient-ids attribute to an empty array
      patientAttendanceElement.setAttribute('selected-patient-ids', '[]');

      // If this component has a button like our check-in component
      const attendanceButton = patientAttendanceElement.querySelector('button');
      if (attendanceButton) {
        attendanceButton.disabled = true;
      }
    }

    // Trigger any potential change events to ensure UI consistency
    this.dispatchCheckboxChangeEvents();
  }

  /**
   * Dispatch change events to ensure UI state is consistent
   */
  private dispatchCheckboxChangeEvents() {
    // Find any checkbox controllers that might need to be notified
    const checkboxControllers = document.querySelectorAll('[data-controller*="patient-list"]');
    if (checkboxControllers.length > 0) {
      // Create a change event
      const changeEvent = new Event('change', { bubbles: true });

      // Dispatch the event on the first controller to trigger any necessary UI updates
      checkboxControllers[0].dispatchEvent(changeEvent);
    }
  }

  /**
   * Show the success message with fade effect
   */
  private showSuccessMessage() {
    // Update the message text
    this.successMessage.innerHTML = '<i class="bi bi-check-circle mr-1"></i>Check in successful';

    // Make the message visible
    this.successMessage.style.display = 'block';
    this.successMessage.style.opacity = '1';

    // Add fade out effect
    setTimeout(() => {
      // Apply transition
      if (this.successMessage) {
        this.successMessage.style.transition = 'opacity 0.5s ease-out';
        this.successMessage.style.opacity = '0';

        // Hide message after fade completes
        setTimeout(() => {
          if (this.successMessage) {
            this.successMessage.style.display = 'none';

            // Reset transition for next time
            setTimeout(() => {
              if (this.successMessage) {
                this.successMessage.style.transition = '';
              }
            }, 50);
          }
        }, 500);
      }
    }, 1000);
  }

  /**
   * Show the clear success message with fade effect
   */
  private showClearSuccessMessage() {
    // Update the message text
    this.successMessage.innerHTML = '<i class="bi bi-check-circle mr-1"></i>Check in cleared';

    // Make the message visible
    this.successMessage.style.display = 'block';
    this.successMessage.style.opacity = '1';

    // Add fade out effect
    setTimeout(() => {
      // Apply transition
      if (this.successMessage) {
        this.successMessage.style.transition = 'opacity 0.5s ease-out';
        this.successMessage.style.opacity = '0';

        // Hide message after fade completes
        setTimeout(() => {
          if (this.successMessage) {
            this.successMessage.style.display = 'none';

            // Reset transition for next time
            setTimeout(() => {
              if (this.successMessage) {
                this.successMessage.style.transition = '';
              }
            }, 50);
          }
        }, 500);
      }
    }, 1000);
  }

  private handleDropdownShown() {
    // We've moved beyond the initial state once the dropdown is opened
    this.initialStatePreserved = false;

    this.appendPatientIds(this.checkinForm);
    this.appendPatientIds(this.clearForm);

    // Update UI based on current selection
    this.updateUIBasedOnCheckinStatus();
  }

  private handleDropdownHidden() {
    this.removePatientIds();
  }

  private updateUIBasedOnCheckinStatus() {
    if (this.selectedPatientIds.length === 0) {
      return;
    }

    // Find all already checked-in patients (those with check icons)
    const checkedInPatientElements = document.querySelectorAll('.bi-check-circle-fill');
    const checkedInPatientIds = new Set();

    // Extract patient IDs from elements that have check icons
    checkedInPatientElements.forEach(icon => {
      const patientRow = icon.closest('li');
      if (patientRow) {
        const checkbox = patientRow.querySelector('input[data-target="patient-list.checkboxes"]');
        if (checkbox) {
          checkedInPatientIds.add(checkbox.value);
        }
      }
    });

    // Check if ANY of the currently selected patients are already checked in
    const anySelectedPatientCheckedIn = this.selectedPatientIds.some(id => checkedInPatientIds.has(id));

    // Check if ALL of the currently selected patients are already checked in
    const allSelectedPatientsCheckedIn = this.selectedPatientIds.length > 0 &&
                                       this.selectedPatientIds.every(id => checkedInPatientIds.has(id));

    // Update UI based on check-in status
    if (allSelectedPatientsCheckedIn) {
      // All selected patients already checked in - disable check-in and show clear option
      this.checkinButton.disabled = true;
      this.checkinNotice.style.display = 'none';
      this.alreadyCheckedInNotice.style.display = 'block';

      // Show clear option since selected patients are checked in
      this.clearButton.style.display = 'block';
      this.clearDivider.style.display = 'block';
    } else {
      // At least one selected patient not checked in - enable check-in
      this.checkinButton.disabled = false;
      this.checkinNotice.style.display = 'block';
      this.alreadyCheckedInNotice.style.display = 'none';

      // ONLY show clear option if at least one of the SELECTED patients is checked in
      if (anySelectedPatientCheckedIn) {
        this.clearButton.style.display = 'block';
        this.clearDivider.style.display = 'block';
      } else {
        // No selected patients are checked in, so hide clear option
        this.clearButton.style.display = 'none';
        this.clearDivider.style.display = 'none';
      }
    }
  }

  /**
   * Add check icons to selected patients immediately without waiting for AJAX response
   */
  private addCheckIconsToSelectedPatients() {
    const currentTime = new Date().toLocaleString();

    // Find all checkboxes for the selected patients
    this.selectedPatientIds.forEach(patientId => {
      // Find the checkbox for this patient
      const checkbox = document.querySelector(`input[value="${patientId}"][data-target="patient-list.checkboxes"]`);

      if (checkbox) {
        // Find the patient row containing this checkbox
        const patientRow = checkbox.closest('li');

        if (patientRow) {
          // Find the container where we need to add the check icon
          const nameContainer = patientRow.querySelector('.d-flex.align-items-center');

          if (nameContainer) {
            // Check if the icon already exists
            const existingIcon = nameContainer.querySelector('.bi-check-circle-fill');

            // Only add if not already checked in
            if (!existingIcon) {
              // Create the check icon element
              const checkIcon = document.createElement('i');
              checkIcon.className = 'bi bi-check-circle-fill ml-2 text-success';
              checkIcon.title = `Checked in at ${currentTime}`;

              // Find the right position to add the icon (after the patient name)
              const patientNameElement = nameContainer.querySelector('a, span');

              if (patientNameElement) {
                patientNameElement.appendChild(checkIcon);
              }
            }
          }
        }
      }
    });
    // Remove edit button after check-in
    this.removeEditButton();
  }

  /**
   * Remove the edit button from the event if it exists
   */
  private removeEditButton() {
    // Look for the edit button with the specific attributes
    const editButton = document.querySelector('button[title="Edit"][data-toggle="modal"][data-target^="#event-edit-form"]');

    if (editButton) {
      // Remove the button entirely
      editButton.remove();
    }
  }

  /**
   * Remove check icons from selected patients immediately without waiting for AJAX response
   */
  private removeCheckIconsFromSelectedPatients() {
    // Find all checkboxes for the selected patients
    this.selectedPatientIds.forEach(patientId => {
      // Find the checkbox for this patient
      const checkbox = document.querySelector(`input[value="${patientId}"][data-target="patient-list.checkboxes"]`);

      if (checkbox) {
        // Find the patient row containing this checkbox
        const patientRow = checkbox.closest('li');

        if (patientRow) {
          // Find the container where the check icon should be
          const nameContainer = patientRow.querySelector('.d-flex.align-items-center');

          if (nameContainer) {
            // Find and remove the icon if it exists
            const existingIcon = nameContainer.querySelector('.bi-check-circle-fill');
            if (existingIcon) {
              existingIcon.remove();
            }
          }
        }
      }
    });

    // If all patients are selected, remove all check icons
    if (this.selectedPatientIds.length === document.querySelectorAll('input[data-target="patient-list.checkboxes"]').length) {
      const allCheckIcons = document.querySelectorAll('.bi-check-circle-fill');
      allCheckIcons.forEach(icon => icon.remove());
    }
  }

  private appendPatientIds(form: HTMLFormElement) {
    for (const id of this.selectedPatientIds) {
      const hiddenInput = createHiddenInput(this.patientIdsName, id);
      form.append(hiddenInput);
    }
  }

  private removePatientIds() {
    const hiddenInputs = Array.from(this.querySelectorAll(`[name="${this.patientIdsName}"]`));
    hiddenInputs.forEach((input) => input.remove());
  }
}